import { useMemo } from 'react'

import type { NavItem } from './menuItems'

import type { Account } from '@models/Account'
import { isUIFeatureVisible } from '@helpers/uiFeatureVisibility'

// Narrow shapes that capture exactly what filterNavItems reads. Used
// in the function signature below so test fixtures can build minimal
// objects without `as unknown as Account` casts. Production callers
// pass full Account / NavItem[]; those satisfy the Pick types
// structurally.
//
// No admin-TIER field here on purpose: nav visibility is decided by
// permissions and the workspace feature toggles, never by which tier
// the viewer sits in. A custom role holding the right codenames gets
// the same nav as the seeded Admin.
export type NavFilterUser = Pick<Account, 'role' | 'permissions' | 'visible_features'>
export type NavFilterItem = Pick<NavItem, 'to' | 'label' | 'show' | 'requirePerm' | 'requireAnyPerm' | 'uiFeatureKey'>

export const useWindowControlsOverlay = () => {
  return useMemo(() => {
    return navigator.windowControlsOverlay !== undefined && navigator.windowControlsOverlay.visible
  }, [navigator.windowControlsOverlay?.visible])
}

/**
 * Resolve a user's effective permission set from /me. Mirrors what
 * `useHasPermission` does — duplicated here as a pure function so
 * filterNavItems can be called outside a React render context (e.g.
 * during memoization with stale closures).
 */
const userPermissions = (user?: NavFilterUser): Set<string> => {
  return new Set(user?.permissions ?? [])
}

export const filterNavItems = <T extends NavFilterItem>(
  items: T[],
  user?: NavFilterUser,
  showNavsWithRestriction?: Record<string, boolean>,
): T[] => {
  const visibleFeatures = user?.visible_features
  const perms = userPermissions(user)

  return items.filter((item) => {
    const isAllowed =
      item.to && showNavsWithRestriction?.[item.to] !== undefined ? showNavsWithRestriction[item.to] : true

    // Workspace feature toggle (Settings → Features) — checked FIRST, before
    // any RBAC gate: a feature the workspace hid for this viewer never shows
    // even when perms would allow it; an enabled (or unset) one falls
    // through to the perm checks below. The admin/user axis is resolved
    // server-side now (`visible_features`), so this no longer picks a
    // column itself.
    if (item.uiFeatureKey && !isUIFeatureVisible(item.uiFeatureKey, visibleFeatures)) {
      return false
    }

    // RBAC permission codename. When set, the user must hold the
    // codename via any of their roles. Falls through to the legacy
    // `show` check if `requirePerm` is unset so non-permission nav
    // items (Dashboard, AI Assistant, Approval Requests, etc.) still
    // work via the legacy role gate.
    // Container entries reachable via several granular perms: visible if
    // the user holds ANY of them. Checked before `requirePerm` so it
    // takes precedence when both are (mistakenly) set.
    if (item.requireAnyPerm !== undefined) {
      return isAllowed && item.requireAnyPerm.some((p) => perms.has(p))
    }

    if (item.requirePerm !== undefined) {
      return isAllowed && perms.has(item.requirePerm)
    }

    return isAllowed && (!item.show || (user && item.show.includes(user.role)))
  })
}
